/**
 * Generate SEO-friendly URL slug from tutor name and ID
 * @param {string} name - Tutor name
 * @param {number} id - Tutor ID
 * @returns {string} URL slug
 */
export const generateTutorSlug = (name, id) => {
  if (!name || !id) return '';
  
  // Convert Armenian/Cyrillic to Latin transliteration
  const translitMap = {
    'ա': 'a', 'բ': 'b', 'գ': 'g', 'դ': 'd', 'ե': 'e', 'զ': 'z', 'է': 'e', 'ը': 'y',
    'թ': 't', 'ժ': 'zh', 'ի': 'i', 'լ': 'l', 'խ': 'kh', 'ծ': 'ts', 'կ': 'k', 'հ': 'h',
    'ձ': 'dz', 'ղ': 'gh', 'ճ': 'ch', 'մ': 'm', 'յ': 'y', 'ն': 'n', 'շ': 'sh', 'ո': 'o',
    'չ': 'ch', 'պ': 'p', 'ջ': 'j', 'ռ': 'r', 'ս': 's', 'վ': 'v', 'տ': 't', 'ր': 'r',
    'ց': 'ts', 'ւ': 'u', 'փ': 'p', 'ք': 'q', 'և': 'ev', 'օ': 'o', 'ֆ': 'f',
    // Uppercase
    'Ա': 'a', 'Բ': 'b', 'Գ': 'g', 'Դ': 'd', 'Ե': 'e', 'Զ': 'z', 'Է': 'e', 'Ը': 'y',
    'Թ': 't', 'Ժ': 'zh', 'Ի': 'i', 'Լ': 'l', 'Խ': 'kh', 'Ծ': 'ts', 'Կ': 'k', 'Հ': 'h',
    'Ձ': 'dz', 'Ղ': 'gh', 'Ճ': 'ch', 'Մ': 'm', 'Յ': 'y', 'Ն': 'n', 'Շ': 'sh', 'Ո': 'o',
    'Չ': 'ch', 'Պ': 'p', 'Ջ': 'j', 'Ռ': 'r', 'Ս': 's', 'Վ': 'v', 'Տ': 't', 'Ր': 'r',
    'Ց': 'ts', 'Ւ': 'u', 'Փ': 'p', 'Ք': 'q', 'Օ': 'o', 'Ֆ': 'f'
  };
  
  // Transliterate Armenian characters
  const transliterated = name
    .split('')
    .map(char => translitMap[char] || char)
    .join('');
  
  // Create slug: lowercase, replace spaces with hyphens, remove special chars
  const slug = transliterated
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')           // Replace spaces with hyphens
    .replace(/[^\w-]+/g, '')        // Remove non-word chars except hyphens
    .replace(/--+/g, '-')           // Replace multiple hyphens with single hyphen
    .replace(/^-+/, '')             // Trim hyphens from start
    .replace(/-+$/, '');            // Trim hyphens from end
  
  return `${slug}-${id}`;
};

/**
 * Extract tutor ID from SEO-friendly slug
 * @param {string} slug - URL slug
 * @returns {number|null} Tutor ID
 */
export const extractIdFromSlug = (slug) => {
  if (!slug) return null;
  const parts = slug.split('-');
  const id = parseInt(parts[parts.length - 1], 10);
  return isNaN(id) ? null : id;
};

/**
 * Generate specialist profile URL
 * @param {Object} tutor - Tutor object with name and id
 * @returns {string} Profile URL
 */
export const getSpecialistUrl = (tutor) => {
  if (!tutor || !tutor.name || !tutor.id) return '#';
  const slug = generateTutorSlug(tutor.name, tutor.id);
  return `/specialist/${slug}`;
};
